import { useEffect, useRef } from "react";

/**
 * Cinematic moving tech background - multi-layered.
 * Layer A: Drifting aurora gradient (CSS animated).
 * Layer B: Perspective holographic grid floor (CSS).
 * Layer C: Neural network canvas with branching data packets.
 * Layer D: SVG HUD overlay with crosshairs, brackets, scanning lines.
 * GPU-friendly, respects prefers-reduced-motion.
 */
export default function NeuralBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    // Mobile detection — denser, snappier, more responsive on small touch screens
    const isMobile =
      ("ontouchstart" in window || navigator.maxTouchPoints > 0) &&
      window.innerWidth < 1024;

    const setSize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    setSize();

    const baseCount = isMobile
      ? Math.min(95, Math.floor((width * height) / 5200))
      : Math.min(140, Math.floor((width * height) / 14000));
    const nodes = [];
    for (let i = 0; i < baseCount; i++) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * (isMobile ? 0.45 : 0.32),
        vy: (Math.random() - 0.5) * (isMobile ? 0.45 : 0.32),
        r: Math.random() * (isMobile ? 1.8 : 1.6) + (isMobile ? 0.7 : 0.6),
        pulse: Math.random() * Math.PI * 2,
      });
    }

    const packets = [];
    const packetCount = isMobile ? 28 : 20;
    for (let i = 0; i < packetCount; i++) {
      packets.push({
        a: Math.floor(Math.random() * baseCount),
        b: Math.floor(Math.random() * baseCount),
        t: Math.random(),
        speed: (isMobile ? 0.0042 : 0.003) + Math.random() * (isMobile ? 0.006 : 0.005),
      });
    }

    const pulseInterval = isMobile ? 2200 : 3500;
    // Touch/mouse interaction scale — larger radius and stronger pull on mobile
    const touchRadius = isMobile ? 290 : 220;
    const touchForce = isMobile ? 0.14 : 0.08;
    const linkLineRadius = isMobile ? 270 : 200;
    const linkLineAlpha = isMobile ? 0.95 : 0.65;

    // Outward radar pulses (rare, dramatic)
    const pulses = [];
    let lastPulseTime = 0;

    const mouse = { x: -9999, y: -9999, active: false };
    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.active = true;
    };
    const onLeave = () => { mouse.active = false; };
    const onTouchMove = (e) => {
      if (e.touches && e.touches.length) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
        mouse.active = true;
      }
    };
    const onTouchEnd = () => {
      // Linger longer on mobile so the bright connection web fades naturally
      setTimeout(() => { mouse.active = false; }, isMobile ? 900 : 600);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);
    window.addEventListener("touchstart", onTouchMove, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });
    window.addEventListener("resize", setSize);

    const linkDist = 160;
    const linkDistSq = linkDist * linkDist;

    let raf;
    let last = performance.now();

    const tick = (now) => {
      const dt = Math.min(40, now - last);
      last = now;
      ctx.clearRect(0, 0, width, height);

      // Soft global glow
      const grad = ctx.createRadialGradient(
        width * 0.5, height * 0.4, 0,
        width * 0.5, height * 0.4, Math.max(width, height) * 0.7
      );
      grad.addColorStop(0, "rgba(80, 30, 160, 0.10)");
      grad.addColorStop(0.6, "rgba(20, 8, 40, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, width, height);

      // Spawn radar pulse occasionally
      if (!reduce && now - lastPulseTime > pulseInterval) {
        lastPulseTime = now;
        pulses.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: 0,
          a: 0.55,
        });
      }
      // Render & evolve pulses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.r += 1.4;
        p.a *= 0.985;
        if (p.a < 0.02 || p.r > 460) {
          pulses.splice(i, 1);
          continue;
        }
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(180, 130, 255, ${p.a})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
        // inner faint ring
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 0.6, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(160, 107, 255, ${p.a * 0.4})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }

      // Update nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        if (!reduce) {
          n.x += n.vx * (dt / 16);
          n.y += n.vy * (dt / 16);
          n.pulse += 0.025;
        }
        if (n.x < 0 || n.x > width) n.vx *= -1;
        if (n.y < 0 || n.y > height) n.vy *= -1;
        n.x = Math.max(0, Math.min(width, n.x));
        n.y = Math.max(0, Math.min(height, n.y));

        if (mouse.active) {
          const dx = mouse.x - n.x;
          const dy = mouse.y - n.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < touchRadius * touchRadius) {
            const f = (1 - d2 / (touchRadius * touchRadius)) * touchForce;
            n.vx += (dx / Math.sqrt(d2 + 0.01)) * f * 0.06;
            n.vy += (dy / Math.sqrt(d2 + 0.01)) * f * 0.06;
          }
        }
        n.vx *= 0.995;
        n.vy *= 0.995;
      }

      // Connections
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkDistSq) {
            const alpha = (1 - d2 / linkDistSq) * 0.5;
            ctx.strokeStyle = `rgba(160, 107, 255, ${alpha})`;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Mouse-link nearby nodes
      if (mouse.active) {
        for (const n of nodes) {
          const dx = mouse.x - n.x;
          const dy = mouse.y - n.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < linkLineRadius * linkLineRadius) {
            const alpha = (1 - d2 / (linkLineRadius * linkLineRadius)) * linkLineAlpha;
            ctx.strokeStyle = `rgba(232, 210, 255, ${alpha})`;
            ctx.lineWidth = isMobile ? 1.4 : 1;
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(n.x, n.y);
            ctx.stroke();
          }
        }
        // Bright halo at the touch point itself
        if (isMobile) {
          const tg = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 60);
          tg.addColorStop(0, "rgba(232, 210, 255, 0.45)");
          tg.addColorStop(0.4, "rgba(160, 107, 255, 0.18)");
          tg.addColorStop(1, "rgba(160, 107, 255, 0)");
          ctx.fillStyle = tg;
          ctx.beginPath();
          ctx.arc(mouse.x, mouse.y, 60, 0, Math.PI * 2);
          ctx.fill();
        }
        // Reset line width for the rest of the frame
        ctx.lineWidth = 1;
      }

      // Nodes
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const flicker = 0.65 + 0.35 * Math.sin(n.pulse);
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(220, 198, 255, ${0.9 * flicker})`;
        ctx.fill();
        const halo = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, n.r * 7);
        halo.addColorStop(0, `rgba(160, 107, 255, ${0.22 * flicker})`);
        halo.addColorStop(1, "rgba(160, 107, 255, 0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r * 7, 0, Math.PI * 2);
        ctx.fill();
      }

      // Data packets w/ trails
      for (let p of packets) {
        const a = nodes[p.a];
        const b = nodes[p.b];
        if (!a || !b) continue;
        const dx = a.x - b.x;
        const dy = a.y - b.y;
        const d2 = dx * dx + dy * dy;
        if (d2 > linkDistSq * 4) {
          p.a = Math.floor(Math.random() * nodes.length);
          p.b = Math.floor(Math.random() * nodes.length);
          p.t = 0;
          continue;
        }
        if (!reduce) p.t += p.speed * (dt / 16);
        if (p.t > 1) {
          p.t = 0;
          p.b = Math.floor(Math.random() * nodes.length);
        }
        const x = a.x + (b.x - a.x) * p.t;
        const y = a.y + (b.y - a.y) * p.t;
        const trailLen = 10;
        for (let k = 0; k < trailLen; k++) {
          const tt = p.t - (k / trailLen) * 0.06;
          if (tt < 0) break;
          const tx = a.x + (b.x - a.x) * tt;
          const ty = a.y + (b.y - a.y) * tt;
          const alpha = (1 - k / trailLen) * 0.85;
          ctx.fillStyle = `rgba(232, 210, 255, ${alpha})`;
          ctx.beginPath();
          ctx.arc(tx, ty, 1.6 - k * 0.1, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = `rgba(255, 255, 255, 1)`;
        ctx.beginPath();
        ctx.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        // bright halo on packet
        const ph = ctx.createRadialGradient(x, y, 0, x, y, 14);
        ph.addColorStop(0, "rgba(220, 190, 255, 0.55)");
        ph.addColorStop(1, "rgba(220, 190, 255, 0)");
        ctx.fillStyle = ph;
        ctx.beginPath();
        ctx.arc(x, y, 14, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    // Pause the animation when the tab is hidden — otherwise this canvas keeps
    // hammering the CPU/GPU in background tabs even though the user can't see
    // it. Resume immediately when the tab regains focus.
    const onVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
        raf = 0;
      } else if (!raf) {
        raf = requestAnimationFrame(tick);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("touchstart", onTouchMove);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
      window.removeEventListener("resize", setSize);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="neural-background"
      className="neural-bg-root"
    >
      {/* Layer A: Drifting aurora gradient */}
      <div className="aurora-layer" />
      <div className="aurora-layer aurora-layer-2" />

      {/* Layer B: Perspective holographic grid floor */}
      <div className="holo-grid">
        <div className="holo-grid-inner" />
      </div>
      <div className="holo-grid holo-grid-top">
        <div className="holo-grid-inner" />
      </div>

      {/* Layer C: Neural network canvas */}
      <canvas ref={canvasRef} className="neural-canvas" />

      {/* Layer D: HUD overlay */}
      <svg className="hud-layer" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="hudLine" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(160,107,255,0)" />
            <stop offset="50%" stopColor="rgba(160,107,255,0.55)" />
            <stop offset="100%" stopColor="rgba(160,107,255,0)" />
          </linearGradient>
          <radialGradient id="hudOrb" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(160,107,255,0.4)" />
            <stop offset="100%" stopColor="rgba(160,107,255,0)" />
          </radialGradient>
        </defs>

        {/* Top brackets */}
        <g className="hud-bracket" stroke="rgba(160,107,255,0.45)" strokeWidth="1.2" fill="none">
          <path d="M40 60 L40 30 L80 30" />
          <path d="M1880 60 L1880 30 L1840 30" />
          <path d="M40 1020 L40 1050 L80 1050" />
          <path d="M1880 1020 L1880 1050 L1840 1050" />
        </g>

        {/* Crosshair */}
        <g className="hud-crosshair" stroke="rgba(160,107,255,0.25)" strokeWidth="0.8" fill="none">
          <line x1="960" y1="0" x2="960" y2="1080" strokeDasharray="2 8" />
          <line x1="0" y1="540" x2="1920" y2="540" strokeDasharray="2 8" />
          <circle cx="960" cy="540" r="180" strokeDasharray="4 6" opacity="0.4" />
        </g>

        {/* Floating orbiting orb */}
        <circle className="hud-orb" cx="0" cy="0" r="160" fill="url(#hudOrb)" />

        {/* Scanning line */}
        <line className="hud-scan" x1="0" y1="0" x2="1920" y2="0" stroke="url(#hudLine)" strokeWidth="2" />

        {/* Coordinate labels */}
        <g className="hud-text" fill="rgba(160,107,255,0.55)" fontSize="11" fontFamily="'Chakra Petch', monospace" letterSpacing="2">
          <text x="55" y="22">SYS · MDCE-AI · v2.6</text>
          <text x="1750" y="22">LAT 33.4152° N</text>
          <text x="55" y="1070">LON -111.7892° W</text>
          <text x="1700" y="1070">SECTOR · MESA-AZ</text>
        </g>
      </svg>

      {/* Subtle noise/grain */}
      <div className="grain-layer" />
    </div>
  );
}
